import { createAction } from '@reduxjs/toolkit'
import { useLocalSearchParams, useRouter } from 'expo-router'
import React, { useState } from 'react'
import { KeyboardAwareScrollView } from 'react-native-keyboard-aware-scroll-view'
import { GroupResource } from '~api/types'
import { FormButton } from '~components/FormButton'
import { FormInput } from '~components/FormInput'
import ModalScreen, { ModalFooter, useStyles } from '~components/ModalScreen'
import { Text, View } from '~components/Themed'
import { ForbiddenError } from '~errors/ForbiddenError'
import { ValidationError } from '~errors/ValidationError'
import { CreateClassContext, DeepRequired } from '~types'
import { useAxios } from '~utils/fetch'

export const setName = createAction<string, 'setName'>('setName')

export default function CreateClassScreen(): React.JSX.Element {
  const styles = useStyles()
  const [name, setName] = useState('')
  const router = useRouter()
  const { context } = useLocalSearchParams<{ context: CreateClassContext }>()

  const [{ loading, error }, execute] = useAxios<DeepRequired<GroupResource>>(
    {
      url: `/api/v1/groups`,
      method: 'POST',
    },
    { manual: true },
  )

  const create = async () => {
    try {
      const { data: folder } = await execute({
        data: {
          name,
        },
      })

      switch (context) {
        case 'chat':
          // Creating a folder within the new chat dialog
          router.navigate({
            pathname: '/folders/new-chat',
            params: {
              folderId: folder?.data.id,
              folderName: folder?.data.name,
            },
          })
          break
        case 'none':
        default:
          router.navigate(`/folders/${folder?.data.id}`, { withAnchor: true })
          break
      }
    } catch (error) {
      if (error instanceof ForbiddenError) {
        router.canDismiss() && router.dismiss()
        router.navigate({
          pathname: 'denied',
          params: {
            message: error.message,
          },
        })
      } else {
        console.info(error)
      }
    }
  }

  return (
    <ModalScreen
      ContainerComponent={KeyboardAwareScrollView}
      Footer={
        <ModalFooter>
          <View></View>
          <FormButton
            onPress={create}
            title={'Create'}
            style={styles.button}
            disabled={name.length === 0 || loading}
          />
        </ModalFooter>
      }
    >
      <View style={styles.inner}>
        <View style={styles.content}>
          <View style={styles.titleContainer}>
            <Text style={styles.title}>Create a folder</Text>
            <Text style={styles.subtitle}>
              You can invite members to join this folder later from the folder settings.
            </Text>
          </View>

          <View>
            <FormInput
              label="Name of folder"
              value={name}
              onChangeText={setName}
              maxLength={128}
              error={error instanceof ValidationError ? error.errors['name'] : error?.message}
            />
          </View>
        </View>
      </View>
    </ModalScreen>
  )
}
